import React, { FC } from "react";
import format from "date-fns/format";

import AppLink from "../AppLink";
import CardsOnFlyerPreviewMedia from "./CardsOnFlyerPreviewMedia";

interface Props {
  page: any;
}

const FlyerPreview: FC<Props> = ({ page }) => {
  let sentFlyerInfo;
  let startDate;
  let startTime;
  let actionText;
  let flyerUrl = "manage";
  let flyerUrlTail = "";

  if (page.event) {
    const currDate = new Date();
    // ignore timezone for allDay events which may subtract one day for timezones west of Greenwich
    const startTimeStr = page.event.allDay ? page.event.startTime?.slice(0, -1) : page.event.startTime;
    const eventStartDate = new Date(startTimeStr);
    const isDiffYear = currDate.getFullYear() !== eventStartDate.getFullYear();
    const dateFormat = `ddd MMM D${isDiffYear ? " YYYY" : ""}`;
    startDate = format(eventStartDate, dateFormat);
    startTime = page.event.allDay ? '' : format(eventStartDate, "h:mm A");
  }

  if (page.type === "sent") {
    actionText = "Manage";
    if (!page.event) {
      flyerUrl = "create";
      sentFlyerInfo = "No time or date";
      actionText = "Finish editing";
    } else if (page.guestsSummary && page.guestsSummary.totalGuests) {
      sentFlyerInfo = `${page.guestsSummary.totalGuests} Going`;
    }
  } else if (page.type === "received") {
    flyerUrl = "go";
    actionText = "Pending response";
    flyerUrlTail = `/pp_g/${page.receiver.guestToken}`;
    if (page.receiver.status === "attending") {
      actionText = "You're attending.";
    } else if (page.receiver.status === "declined") {
      actionText = "You're not going";
    }
  }

  // Using opacity instead of text colors in here to make it easier to manage the highlight states.
  return (
    <AppLink
      className="block last:mb-0"
      href={`/${flyerUrl}/${page.id}${flyerUrlTail}`}
      tracking={{
        id: "upcomingFlyer",
        upcoming_event_type: "draft",
        block_name: "account_drawer",
        block_row: "account_drawer",
      }}
    >
      <div className="flex w-64 w-full h-28 shadow hover:shadow-md">
        {page.artifacts?.media || page.artifacts?.cardFront ? (
          <PreviewImage
            artifacts={page.artifacts}
            productType={page.productType}
          />
        ) : (
          <div className="bg-dove w-28 h-28" />
        )}
        <div className="flex flex-col header-xxsmall overflow-hidden p-2">
          <h3 className="heading-xxsmall mb-1 truncate">
            {page.event ? page.event.name : "Untitled Event"}
          </h3>
          {sentFlyerInfo ? (
            <div className="paragraph-small text-lapel">{sentFlyerInfo}</div>
          ) : (
            <>
              <div className="paragraph-small opacity-80">{startDate}</div>
              <div className="paragraph-small opacity-80">{startTime}</div>
            </>
          )}
          {actionText && (
            <div className="flex flex-1 highlight-link items-end">
              {actionText}
            </div>
          )}
        </div>
      </div>
    </AppLink>
  );
};

interface PreviewImageProps {
  artifacts: any;
  productType: string;
}

const PreviewImage: FC<PreviewImageProps> = ({ artifacts, productType }) => {
  if (productType === "card") {
    return <CardsOnFlyerPreviewMedia artifacts={artifacts} />;
  } else {
    const src = artifacts?.media?.asset?.renditions?.jpegSmall?.url;
    return <img className="w-28 h-28" src={src} alt="Flyer media" />;
  }
};

export default React.memo(FlyerPreview);
